/**
 * Ogg-Opus chunk encoding via WebCodecs (`AudioEncoder`): native, faster
 * than realtime, and needs no CSP concession — unlike WASM encoders, which
 * would require 'wasm-unsafe-eval' in script-src.
 *
 * At 32 kbps mono a 30-minute speech chunk is ~7 MB versus ~57 MB as PCM16
 * WAV; transcription accuracy is unaffected (Whisper bills per minute and
 * handles compressed speech fine). Callers must feature-probe first and
 * fall back to WAV where WebCodecs is missing (see transcribe.ts).
 */
import { muxOggOpus, type OggOpusPacket } from "./ogg-opus";

/** 32 kbps mono: transparent for wideband speech ASR. */
const OPUS_BITRATE = 32_000;

/** Feed ~1 s of samples per AudioData; the encoder does its own framing. */
const ENCODER_INPUT_FRAMES = 16_000;

function opusConfig(sampleRate: number): AudioEncoderConfig {
  // Opus accepts 8/12/16/24/48 kHz input; the decode pipeline always
  // produces 16 kHz mono (lib/client/audio/decode.ts).
  return { codec: "opus", sampleRate, numberOfChannels: 1, bitrate: OPUS_BITRATE };
}

/**
 * Feature probe: encodes 100 ms of silence end-to-end. `isConfigSupported`
 * alone doesn't prove the encode→mux pipeline works in this browser, and a
 * mid-recording failure would waste already-transcribed chunks.
 */
export async function isOpusEncodeSupported(sampleRate: number): Promise<boolean> {
  if (typeof AudioEncoder === "undefined" || typeof AudioData === "undefined") {
    return false;
  }
  try {
    const { supported } = await AudioEncoder.isConfigSupported(opusConfig(sampleRate));
    if (!supported) return false;
    const silence = new Float32Array(Math.round(sampleRate / 10));
    const blob = await encodeOpusOggMono(silence, sampleRate);
    return blob.size > 0;
  } catch {
    return false;
  }
}

/** Encode float mono samples ([-1, 1]) into an Ogg-Opus blob. */
export async function encodeOpusOggMono(
  samples: Float32Array<ArrayBuffer>,
  sampleRate: number,
): Promise<Blob> {
  const packets: OggOpusPacket[] = [];
  let encodeError: Error | null = null;

  const encoder = new AudioEncoder({
    output: (chunk) => {
      const data = new Uint8Array(chunk.byteLength);
      chunk.copyTo(data);
      packets.push({
        data,
        // chunk.duration is µs; the Ogg-Opus granule domain is 48 kHz
        // samples whatever the input rate.
        granuleSamples: Math.round(((chunk.duration ?? 0) * 48_000) / 1_000_000),
      });
    },
    error: (error) => {
      encodeError = error instanceof Error ? error : new Error(String(error));
    },
  });

  try {
    encoder.configure(opusConfig(sampleRate));
    for (let offset = 0; offset < samples.length; offset += ENCODER_INPUT_FRAMES) {
      const numberOfFrames = Math.min(ENCODER_INPUT_FRAMES, samples.length - offset);
      const frame = new AudioData({
        format: "f32",
        sampleRate,
        numberOfFrames,
        numberOfChannels: 1,
        timestamp: Math.round((offset / sampleRate) * 1_000_000),
        data: samples.subarray(offset, offset + numberOfFrames),
      });
      try {
        encoder.encode(frame);
      } finally {
        frame.close();
      }
    }
    await encoder.flush();
  } catch (error) {
    throw encodeError ?? error;
  } finally {
    if (encoder.state !== "closed") encoder.close();
  }
  if (encodeError) throw encodeError;

  return new Blob([muxOggOpus(packets, sampleRate)], { type: "audio/ogg" });
}
